import { ErrorBoundary, For, Match, Show, Suspense, Switch, createEffect, createMemo, createSignal, lazy, on, onCleanup, onMount, untrack } from "solid-js";
import type { Component, JSX } from "solid-js";
import type { Me, TableInvite } from "./api";
import { isTurn } from "./game/clock";
import { ApiError, lobby, me as fetchMe, setSettings, signOut, startGuest, tableAction, tableInvite } from "./api";
import { createGameSession } from "./game/session";
import { createQueue } from "./game/queue";
import { createRematches } from "./game/rematches";
import { QueueBar } from "./ui/QueueBar";
import { RematchBubbles } from "./ui/RematchBubble";
import { TablePage } from "./pages/TablePage";
import { Brand } from "./ui/Brand";
import { milestone, tilesDrawn } from "./ui/milestones";
import stillTable from "./assets/stills/still-table.webp";
import stillReview from "./assets/stills/still-review.webp";
import stillResult from "./assets/stills/still-result.webp";
import { Frame, SiteContext } from "./ui/Frame";
import { t } from "./strings";
import { DEFAULT_SETTINGS } from "./game/auto";
import type { PlaySettings } from "./game/auto";

// Pages a first visit does not need load when visited (design 8 item 10): the table stays in the first download; Home, the
// replay (with its verifier), the rules, profiles and settings come in their own chunks. Home left the first download on
// 2026-11-03 (design 15 §16.1): its rows and cards were bytes in every table's first visit, on a slow line where the tiles
// wait on each one, and a first visitor lands on the signed-out page, a returning one on the worker's copy.
const Home = lazy(() => import("./pages/Home").then((m) => ({ default: m.Home })));
const ReplayPage = lazy(() => import("./pages/ReplayPage").then((m) => ({ default: m.ReplayPage })));
const ProfilePage = lazy(() => import("./pages/ProfilePage").then((m) => ({ default: m.ProfilePage })));
const Settings = lazy(() => import("./pages/Settings").then((m) => ({ default: m.Settings })));
const Rules = lazy(() => import("./pages/Rules").then((m) => ({ default: m.Rules })));
const Grading = lazy(() => import("./pages/Grading").then((m) => ({ default: m.Grading })));
const History = lazy(() => import("./pages/History").then((m) => ({ default: m.History })));
const SignInPage = lazy(() => import("./pages/SignInPage").then((m) => ({ default: m.SignInPage })));
const EmailLink = lazy(() => import("./pages/SignInPage").then((m) => ({ default: m.EmailLink })));
const NameStep = lazy(() => import("./pages/NameStep").then((m) => ({ default: m.NameStep })));
const Leaderboard = lazy(() => import("./pages/Leaderboard").then((m) => ({ default: m.Leaderboard })));
const Start = lazy(() => import("./pages/Start").then((m) => ({ default: m.Start })));
const Assessment = lazy(() => import("./pages/Assessment").then((m) => ({ default: m.Assessment })));
const Learn = lazy(() => import("./pages/Learn").then((m) => ({ default: m.Learn })));
// A table in play with no server (design 11 §9): the layout's workbench and the startup budget's page.
const LandingTable = lazy(() => import("./ui/LandingTable"));
const Kit = lazy(() => import("./pages/Kit").then((m) => ({ default: m.Kit })));
const TableFixture = lazy(() => import("./pages/TableFixture").then((m) => ({ default: m.TableFixture })));
// A page that could not be loaded, or broke: said, with the way on. It used to leave "…", or nothing at all, with no way out.
function PageFailed(props: { error: unknown }) {
  console.error(props.error);
  return (
    <Frame title={t.pageFailedTitle}>
      <p>{t.pageFailed}</p>
      <button type="button" onClick={() => location.reload()}>
        {t.reload}
      </button>
    </Frame>
  );
}
const Later = (props: { children: JSX.Element }) => (
  <ErrorBoundary fallback={(e) => <PageFailed error={e} />}>
    <Suspense fallback={<main class="page">…</main>}>{props.children}</Suspense>
  </ErrorBoundary>
);

// Routes (design 7 §6.1): "/" the lobby (or sign-in), "/t/<token>" a table, "/p/<public_id>" a profile,
// "/g/<game_id>" a replay, "/settings", "/about". A signed-in tab keeps one game connection open on every
// page; when a game starts for the account, the tab goes to its table.

function createPath(): [() => string, (p: string) => void] {
  const [path, setPath] = createSignal(location.pathname);
  const onPop = () => setPath(location.pathname);
  addEventListener("popstate", onPop);
  onCleanup(() => removeEventListener("popstate", onPop));
  const go = (p: string) => {
    const to = new URL(p, location.href);
    if (to.pathname + to.search + to.hash !== location.pathname + location.search + location.hash) history.pushState(null, "", p);
    setPath(to.pathname);
    if (!to.hash) scrollTo(0, 0);
  };
  return [path, go];
}

export function App(props: { fixture?: Component }) {
  const [path, go] = createPath();
  const [account, setAccount] = createSignal<Me | null | undefined>(undefined);
  // The account as the server tells it: null when signed out. A request that fails (the server unreachable,
  // restarting) changes nothing: a page in a game keeps it and shows the connection lost while the socket
  // reconnects (it used to show the sign-in page, and stay there); a first load tries again.
  const refresh = (): Promise<void> =>
    fetchMe().then((a) => void setAccount(a), () => {
      if (account() === undefined) setTimeout(() => void refresh(), 2000);
    });
  void refresh();

  const ready = () => {
    const a = account();
    return !!a && !a.needs_profile;
  };
  const onGame = () => {
    queue.gameStarted();
    lobby().then((l) => {
      if (l.current && location.pathname !== `/t/${l.current}`) go(`/t/${l.current}`);
    }, () => {});
  };
  const queue = createQueue(() => ready() && !account()?.guest, () => account()?.settings ?? DEFAULT_SETTINGS, go);
  const rematches = createRematches(() => ready() && !account()?.guest);
  const game = createGameSession(ready, onGame, () => account()?.settings ?? DEFAULT_SETTINGS);
  // Once for each time it goes: the refresh gives a new account, and an effect that read ready() ran again for that, a hundred times a second.
  createEffect(
    on(game.status, (st) => {
      if ((st === "closed" || st === "signed-out") && untrack(ready)) void refresh(); // a refused socket may mean the session ended; this one says so
    }),
  );

  // Each page's name in its tab and in the history, and the focus moved to it: a change of page said nothing, and left the focus on nothing (UX review 2026-11-02).
  let firstPage = true;
  createEffect(
    on(path, (p) => {
      const name = pageTitle(p);
      if (name !== null) document.title = name ? `${name} · ${t.siteTitle}` : t.siteTitle;
      if (firstPage) return void (firstPage = false);
      setTimeout(() => {
        const target = document.querySelector<HTMLElement>("main h1") ?? document.querySelector<HTMLElement>("main");
        if (!target || (document.activeElement && document.activeElement !== document.body)) return;
        target.tabIndex = -1;
        target.focus({ preventScroll: true });
      }, 60);
    }),
  );
  const parts = createMemo(() => path().split("/"));
  const section = () => parts()[1] ?? "";
  const arg = () => parts()[2] ?? "";
  const onSettings = (s: PlaySettings) => {
    const a = account();
    if (!a) return;
    setAccount({ ...a, settings: s }); // at once; the server keeps it
    setSettings(s).catch(() => void refresh());
  };

  const site = {
    me: account,
    path,
    go,
    signOut: () => void signOut().then(() => {
      setAccount(null);
      go("/");
    }),
    queue,
    rematches,
    refreshMe: () => void refresh(),
    gameGoing: () => {
      const v = game.view();
      if (!v.hand || v.gameEnd || v.phase !== "playing") return null;
      return v.prompt && !v.prompt.sent && isTurn(v.prompt.prompt.kind) ? ("turn" as const) : ("playing" as const);
    },
    backToGame: () => {
      void lobby().then((l) => {
        if (l.current) go(`/t/${l.current}`);
      });
    },
  };

  return (
    <SiteContext.Provider value={site}>
    <QueueBar queue={queue} path={path()} go={go} />
    <RematchBubbles rematches={rematches} path={path()} go={go} />
    <ErrorBoundary fallback={(e) => <PageFailed error={e} />}>
    <Switch fallback={path() === "/" ? <Home me={account()!} go={go} /> : <NotFound go={go} />}>
      <Match when={path() === "/about"}>
        <About />
      </Match>
      <Match when={path() === "/rules"}>
        <Later>
          <Rules go={go} />
        </Later>
      </Match>
      <Match when={path() === "/grading"}>
        <Later>
          <Grading go={go} />
        </Later>
      </Match>
      <Match when={path() === "/leaderboard"}>
        <Later>
          <Leaderboard go={go} />
        </Later>
      </Match>
      <Match when={section() === "learn"}>
        <Later>
          <Learn go={go} page={arg()} />
        </Later>
      </Match>
      <Match when={path() === "/dev/kit"}>
        <Later>
          <Kit />
        </Later>
      </Match>
      <Match when={path() === "/dev/table"}>
        <Show when={props.fixture} fallback={<Later><TableFixture /></Later>}>
          {(Fixture) => {
            const F = Fixture();
            return <F />;
          }}
        </Show>
      </Match>
      <Match when={path() === "/signin/email"}>
        <Later>
          <EmailLink go={go} onSignedIn={setAccount} />
        </Later>
      </Match>
      <Match when={account() === undefined}>
        <AccountWait />
      </Match>
      <Match when={section() === "p" && arg() && parts()[3] === "games" && !account()?.needs_profile}>
        <Later>
          <History go={go} of={arg()} />
        </Later>
      </Match>
      <Match when={section() === "p" && arg() && !account()?.needs_profile}>
        <Later>
          <ProfilePage id={arg()} go={go} />
        </Later>
      </Match>
      <Match when={section() === "g" && arg() && !account()?.needs_profile}>
        <Later>
          <ReplayPage id={arg()} me={account() ?? null} go={go} />
        </Later>
      </Match>
      <Match when={path() === "/signin"}>
        <Later>
          <SignInPage me={account()} go={go} />
        </Later>
      </Match>
      <Match when={account() === null}>
        <SignIn
          invite={section() === "t" ? arg() : undefined}
          reason={["games", "settings", "assessment"].includes(section()) ? t.signInToSee : undefined}
          onPlay={(a, table) => {
            setAccount(a);
            go(table ? `/t/${table}` : a.settings.experience ? "/" : "/start");
          }}
        />
      </Match>
      <Match when={account()?.needs_profile}>
        <Later>
          <NameStep me={account()!} onDone={setAccount} />
        </Later>
      </Match>
      <Match when={path() === "/assessment"}>
        <Later>
          <Assessment me={account()!} go={go} />
        </Later>
      </Match>
      <Match when={path() === "/start"}>
        <Later>
          <Start me={account()!} go={go} onSettings={onSettings} />
        </Later>
      </Match>
      <Match when={section() === "start" && arg() === "lesson"}>
        <Later>
          <Learn go={go} first page={parts()[3] ?? ""} />
        </Later>
      </Match>
      <Match when={section() === "t" && arg()}>
        <TablePage token={arg()} me={account()!} game={game} go={go} onSettings={onSettings} queue={queue} rematches={rematches} />
      </Match>
      <Match when={path() === "/games"}>
        <Later>
          <History go={go} />
        </Later>
      </Match>
      <Match when={section() === "settings"}>
        <Later>
          <Settings
            me={account()!}
            onSettings={onSettings}
            onDone={setAccount}
          />
        </Later>
      </Match>
    </Switch>
    </ErrorBoundary>
    </SiteContext.Provider>
  );
}

/** While the account loads. It retries by itself, and after a moment says so: a lone "…" stayed for as long as the server was out of reach. */
function AccountWait() {
  const [slow, setSlow] = createSignal(false);
  const id = setTimeout(() => setSlow(true), 4_000);
  onCleanup(() => clearTimeout(id));
  return <main class="page">{slow() ? t.stillTrying : "…"}</main>;
}

/** A page's name for its tab: null where the page says for itself, as a table does. */
function pageTitle(path: string): string | null {
  const section = path.split("/")[1] ?? "";
  if (section === "t") return null;
  const names: Record<string, string> = { "": t.navHome, games: t.historyTitle, leaderboard: t.navBoard, learn: t.navLearn, rules: t.navRules, grading: t.gradingTitle, settings: t.settings, about: t.about, p: t.profile, g: t.gameTitle, signin: t.signIn, assessment: t.assessmentTitle, start: t.navLearn };
  return names[section] ?? "";
}

function SignIn(props: { invite?: string; reason?: string; onPlay: (a: Me, table?: string) => void }) {
  const [invite, setInvite] = createSignal<TableInvite | null>(null);
  const [badLink, setBadLink] = createSignal(false);
  createEffect(() => {
    const token = props.invite;
    if (token) tableInvite(token).then(setInvite, (err: unknown) => {
      if (err instanceof ApiError && err.status === 404) setBadLink(true);
    });
  });
  const stopped = (i: TableInvite) => (i.state === "ended" ? t.inviteEnded : i.state === "closed" ? t.inviteClosed : i.state === "full" ? t.inviteFull : t.inviteStarted);
  const failed = new URLSearchParams(location.search).has("signin");
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal("");
  const play = () => {
    setBusy(true);
    setError("");
    startGuest().then((a) => props.onPlay(a), (err: unknown) => {
      setBusy(false);
      setError(err instanceof Error ? err.message : String(err));
    });
  };
  const join = () => {
    setBusy(true);
    setError("");
    const token = props.invite!;
    startGuest()
      .then(async (a) => {
        await tableAction(token, "join");
        // Someone new comes in by a friend's link with hints on: no question was asked, and nothing said to tap twice (review 2026-11-01).
        if (a.settings.experience) return props.onPlay(a, token);
        const settings = { ...a.settings, hints: true };
        void setSettings(settings).catch(() => {});
        props.onPlay({ ...a, settings }, token);
      })
      .catch((err: unknown) => {
        setBusy(false);
        setError(err instanceof Error ? err.message : String(err));
      });
  };
  // The table behind the page comes after the page's words and buttons are drawn (design 14 §9.1, design 11's budget).
  const [behind, setBehind] = createSignal(false);
  onMount(() => {
    milestone("mj:landing");
    const id = requestAnimationFrame(() => setTimeout(() => setBehind(true), 0));
    onCleanup(() => cancelAnimationFrame(id));
  });
  const [late, setLate] = createSignal(false);
  onMount(() => {
    const id = setTimeout(() => setLate(true), 4000);
    onCleanup(() => clearTimeout(id));
  });
  const stills = () => tilesDrawn() || late();
  // The landing (design 14 §9.1): the real table playing behind "Play now"; what the site is below the fold.
  return (
    <Frame class="signin">
      <Show when={behind()}>
        <Suspense>
          <LandingTable />
        </Suspense>
      </Show>
      <section class="hero">
        <Show when={!props.invite}>
          <h1 class="sr-only">{t.appName}</h1>
        </Show>
        <Brand size={88} />
        <Show when={badLink()}>
          <p class="notice" role="alert">{t.inviteBad}</p>
        </Show>
        <Show when={props.reason}>
          <p class="notice" role="alert">{props.reason}</p>
        </Show>
        <Show when={invite()} fallback={<p class="tagline">{t.tagline}</p>}>
          {(i) => (
            <div class="invite">
              <h1>{i().open ? t.invited(i().host) : stopped(i())}</h1>
              <p class="tagline">{i().rules === "wrc-2025" ? t.wrc : t.tenhou} · {i().length === "east" ? t.east : t.eastSouth} · {t.clocksShort[i().clock ?? "standard"]}</p>
              <Show when={i().open}>
                <p class="small">{t.invitedSeats(i().seated)}</p>
              </Show>
              <Show when={i().game_id}>
                {(g) => (
                  <p>
                    <a href={`/g/${g()}`}>{t.watchReplay}</a>
                  </p>
                )}
              </Show>
            </div>
          )}
        </Show>
        <Show when={failed}>
          <p class="notice" role="alert">{t.signInFailed}</p>
        </Show>
        <Show when={error()}>
          <p class="notice" role="alert">{error()}</p>
        </Show>
        <Show
          when={invite()?.open}
          fallback={
            <button type="button" class="play-now" disabled={busy()} onClick={play}>
              {t.playNow}
            </button>
          }
        >
          <button type="button" class="play-now" disabled={busy()} onClick={join}>
            {t.joinAsGuest}
          </button>
        </Show>
        {/* What a player of other sites asks first (design 15 §1.1, UI review V10); Sign in is in the top bar and the sidebar. */}
        <p class="trust">{t.trustLine}</p>
      </section>
      {/* The product itself, in three real renders (design 15 §16.1 step 19): asked for once the table behind has its tiles, or
          after four seconds, so that a first visit's budget (design 11 §9) never pays for them. */}
      <Show when={stills()}>
        <ul class="stills">
          <For each={[stillTable, stillReview, stillResult]}>
            {(src, i) => (
              <li>
                <img src={src} width="600" height="375" alt={t.landingStills[i()]!.alt} decoding="async" />
                <span>{t.landingStills[i()]!.caption}</span>
              </li>
            )}
          </For>
        </ul>
      </Show>
      <ul class="features">
        <For each={t.landingFeatures}>
          {(f) => (
            <li>
              <strong>{f.title}</strong>
              <span>{f.text}</span>
            </li>
          )}
        </For>
      </ul>
    </Frame>
  );
}

function NotFound(props: { go: (p: string) => void }) {
  return (
    <Frame title={t.notFound}>
      <p>{t.noSuchPage}</p>
      <button type="button" onClick={() => props.go("/")}>
        {t.home}
      </button>
    </Frame>
  );
}

function About() {
  return (
    <Frame title={t.about} class="prose">
      <p>{t.aboutAlpha}</p>
      <h2>{t.privacyTitle}</h2>
      <For each={t.privacy}>{(para) => <p>{para}</p>}</For>
      <h2>{t.creditsTitle}</h2>
      <p>{t.credits}</p>
    </Frame>
  );
}
